/**
 * API 응답 처리 유틸리티
 * 공통으로 사용되는 API 응답 unwrap 및 에러 처리 함수
 */

/**
 * API 응답에서 데이터를 추출하고 에러를 처리합니다.
 * @param {Object} response - API 응답 객체
 * @param {Object} options - 옵션 객체
 * @param {string} options.defaultMessage - 기본 에러 메시지
 * @param {boolean} options.includeMessage - 응답에 message 포함 여부
 * @returns {Object|Array|null} 추출된 데이터
 * @throws {Error} 응답이 실패한 경우
 */
export function unwrapResponse(response, { defaultMessage = "요청 처리 중 오류가 발생했습니다.", includeMessage = false } = {}) {
    if (!response) {
        throw new Error(defaultMessage || "응답이 존재하지 않습니다.");
    }

    const payload = response.data ?? response;

    if (payload?.success === false) {
        const message =
            payload?.error?.message || payload?.message || defaultMessage || "요청 처리 중 오류가 발생했습니다.";
        const error = new Error(message);
        
        if (payload?.error?.code) {
            error.code = payload.error.code;
        }
        error.payload = payload;
        throw error;
    }

    // includeMessage 옵션이 true인 경우 message와 success도 함께 반환
    if (includeMessage) {
        return {
            data: payload?.data ?? payload ?? null,
            message: payload?.message || null,
            success: payload?.success ?? true,
        };
    }

    return payload?.data ?? payload ?? null;
}

/**
 * API 응답에서 성공 데이터를 추출합니다 (간단한 버전)
 * @param {Object} response - API 응답 객체
 * @param {string} defaultMessage - 기본 에러 메시지
 * @returns {Object|Array|null} 추출된 데이터
 * @throws {Error} 응답이 실패한 경우
 */
export function unwrapSuccess(response, defaultMessage = "요청 처리 중 오류가 발생했습니다.") {
    return unwrapResponse(response, { defaultMessage });
}

/**
 * 에러 객체에서 사용자 친화적인 메시지를 추출합니다.
 * @param {Error} error - 에러 객체
 * @param {string} fallbackMessage - 기본 메시지
 * @returns {string} 에러 메시지
 */
export function extractErrorMessage(error, fallbackMessage = "요청 처리 중 오류가 발생했습니다.") {
    const payload =
        error?.response?.data?.error ||
        error?.response?.data ||
        error?.payload?.error ||
        error?.payload ||
        null;

    const baseMessage =
        payload?.message ||
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        fallbackMessage;

    const details = payload?.details;
    if (Array.isArray(details) && details.length > 0) {
        const detailMessages = details
            .map((item) => item?.message)
            .filter(Boolean);
        if (detailMessages.length > 0) {
            return `${baseMessage} (${detailMessages.join(", ")})`;
        }
    }

    return baseMessage;
}

/**
 * API 응답에서 배열 데이터를 안전하게 추출합니다.
 * 다양한 응답 구조를 처리합니다.
 * @param {Object} data - 응답 데이터
 * @param {string[]} possibleKeys - 가능한 키 목록
 * @returns {Array} 배열 데이터
 */
export function extractArrayData(data, possibleKeys = ["items", "data", "organizations", "boundaries", "inventories", "targets", "versions"]) {
    if (!data) return [];
    if (Array.isArray(data)) return data;

    for (const key of possibleKeys) {
        if (Array.isArray(data[key])) {
            return data[key];
        }
        if (data[key]?.data && Array.isArray(data[key].data)) {
            return data[key].data;
        }
    }

    return [];
}

/**
 * EVIDENCE_MISSING 에러 hint에서 forceConfirm 기대값 파싱
 * hint 예: "증빙 누락을 무시하고 생성하려면 body에 { \"forceConfirm\": \"CONFIRM\" }을 포함하세요."
 * @param {string} [hint] - 서버에서 내려준 hint 문자열
 * @returns {string} forceConfirm 값 (파싱 실패 시 'CONFIRM' 폴백)
 */
export function parseForceConfirmFromHint(hint) {
    if (!hint || typeof hint !== "string") return "CONFIRM";
    const m = hint.match(/"forceConfirm"\s*:\s*"([^"]+)"/);
    return m ? m[1] : "CONFIRM";
}
